import { c as _c } from "react/compiler-runtime";
import ButtonComponent, { IconSize } from '../button/button/button.component';
import React, { CSSProperties, ReactElement, useRef } from 'react';
import { AiLabel } from '@spa/pages-converters/cms/components/convertCmsImage';
import { ButtonType } from '../button/types/ButtonType';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import { ImageAiMarkerComponent } from '@spa/components/image-ai-marker/image-ai-marker.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './video-player.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';
export interface Props {
  readonly url: string;
  readonly previewImageSrc: string | null;
  readonly previewImageAiLabel?: AiLabel | null;
  readonly alt: string;
  readonly height: number;
  readonly width: number;
  readonly autoplay: boolean;
  readonly muted: boolean;
  readonly editorMode: boolean;
  readonly dateTo: number | null;
  readonly dateFrom: number | null;
}
export default function VideoPlayerComponent(t0) {
  const $ = _c(84);
  const {
    url,
    previewImageSrc,
    previewImageAiLabel,
    alt,
    height,
    width,
    autoplay,
    muted,
    editorMode,
    dateFrom,
    dateTo
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("video-player");
  const t1 = `${width}px`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      maxWidth: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const containerStyles = t2;
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  const initialPlaybackActive = autoplay;
  const t3 = muted || autoplay;
  let t4;
  if ($[2] !== initialPlaybackActive || $[3] !== t3) {
    t4 = {
      currentTime: 0,
      duration: 0,
      isInFocus: false,
      isLoaded: initialPlaybackActive,
      isMuted: t3,
      isPlaying: initialPlaybackActive,
      progress: 0,
      volume: 1
    };
    $[2] = initialPlaybackActive;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [state, updateState] = useObjectReducer(t4);
  const {
    currentTime,
    duration,
    isInFocus,
    isLoaded,
    isMuted,
    isPlaying,
    progress,
    volume
  } = state;
  const hideControlsTimeoutRef = useRef(null);
  const isPosterShown = previewImageSrc !== null && !isPlaying && currentTime === 0;
  let t5;
  if ($[5] !== updateState) {
    t5 = () => {
      if (videoRef.current) {
        const video = videoRef.current;
        const nextProgress = video.currentTime / video.duration;
        if (progressBarRef.current) {
          progressBarRef.current.style.setProperty("--background-size", `${nextProgress * 100}%`);
        }
        updateState({
          currentTime: video.currentTime,
          progress: nextProgress
        });
        if (nextProgress === 1) {
          updateState({
            isPlaying: false
          });
        }
      }
    };
    $[5] = updateState;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const updateProgress = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => {
      if (videoRef.current) {
        videoRef.current.currentTime = event.target.valueAsNumber * videoRef.current.duration;
      }
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleProgressChange = t6;
  let handleProgressKeyDown;
  let handleProgressMouseDown;
  let handleProgressMouseUp;
  let togglePlayPause;
  if ($[8] !== isPlaying || $[9] !== updateState) {
    handleProgressKeyDown = event_0 => {
      bb24: switch (event_0.code) {
        case "Space":
          {
            event_0.preventDefault();
            togglePlayPause();
            break bb24;
          }
        case "ArrowRight":
          {
            updateState(_temp);
            break bb24;
          }
        case "ArrowLeft":
          {
            updateState(_temp2);
          }
      }
    };
    let t7;
    if ($[14] !== isPlaying) {
      t7 = () => {
        if (videoRef.current && isPlaying) {
          videoRef.current.pause();
        }
      };
      $[14] = isPlaying;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    handleProgressMouseDown = t7;
    handleProgressMouseUp = () => {
      if (videoRef.current && isPlaying) {
        videoRef.current.play().catch(() => {
          updateState({
            isPlaying: false
          });
        });
      }
    };
    togglePlayPause = () => {
      const video_0 = videoRef.current;
      if (video_0) {
        if (isPlaying) {
          video_0.pause();
        } else {
          video_0.play().catch(() => {
            updateState({
              isPlaying: false
            });
          });
        }
        updateState({
          isPlaying: !isPlaying
        });
      }
    };
    $[8] = isPlaying;
    $[9] = updateState;
    $[10] = handleProgressKeyDown;
    $[11] = handleProgressMouseDown;
    $[12] = handleProgressMouseUp;
    $[13] = togglePlayPause;
  } else {
    handleProgressKeyDown = $[10];
    handleProgressMouseDown = $[11];
    handleProgressMouseUp = $[12];
    togglePlayPause = $[13];
  }
  let t7;
  if ($[16] !== isMuted || $[17] !== updateState) {
    t7 = () => {
      updateState({
        isMuted: !isMuted
      });
    };
    $[16] = isMuted;
    $[17] = updateState;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const toggleMute = t7;
  let t8;
  if ($[19] !== updateState) {
    t8 = e => {
      const video_1 = videoRef.current;
      const newVolume = parseFloat(e.target.value);
      updateState({
        isMuted: false
      });
      if (video_1) {
        video_1.volume = newVolume;
        updateState({
          volume: newVolume
        });
        if (newVolume === 0) {
          updateState({
            isMuted: true
          });
        }
      }
    };
    $[19] = updateState;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleVolumeChange = t8;
  const formatTime = _temp3;
  let t9;
  if ($[21] !== updateState) {
    t9 = () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
      updateState({
        isInFocus: true
      });
    };
    $[21] = updateState;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleFocus = t9;
  let t10;
  if ($[23] !== updateState) {
    t10 = () => {
      hideControlsTimeoutRef.current = setTimeout(() => {
        updateState({
          isInFocus: false
        });
      }, 1000);
    };
    $[23] = updateState;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleFocusOut = t10;
  let t11;
  if ($[25] !== dateFrom || $[26] !== dateTo || $[27] !== editorMode) {
    t11 = editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />;
    $[25] = dateFrom;
    $[26] = dateTo;
    $[27] = editorMode;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== formatMessage || $[30] !== isPlaying || $[31] !== togglePlayPause) {
    t12 = !isPlaying && <div className={styles["video-player__play-button"]}><ImageComponent src="/assets/icons/video-play.svg" alt={formatMessage("play")} width={64} height={64} loading="lazy" onClick={togglePlayPause} /></div>;
    $[29] = formatMessage;
    $[30] = isPlaying;
    $[31] = togglePlayPause;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== isPosterShown || $[34] !== previewImageAiLabel) {
    t13 = isPosterShown && previewImageAiLabel && <ImageAiMarkerComponent show={previewImageAiLabel.enabled} position={previewImageAiLabel.position} style={previewImageAiLabel.style} />;
    $[33] = isPosterShown;
    $[34] = previewImageAiLabel;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = previewImageSrc ?? undefined;
  const t15 = isPlaying ? "playing" : "paused";
  const t16 = isMuted || autoplay;
  let t17;
  if ($[36] !== updateState) {
    t17 = () => {
      updateState({
        duration: videoRef.current?.duration ?? 0,
        isLoaded: true
      });
    };
    $[36] = updateState;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== url) {
    t18 = <source src={url} type="video/mp4" data-testid="video-player-source" />;
    $[38] = url;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== formatMessage) {
    t19 = formatMessage("incompatibleMessage");
    $[40] = formatMessage;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t19) {
    t20 = <span>{t19}</span>;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== alt || $[45] !== autoplay || $[46] !== handleFocus || $[47] !== handleFocusOut || $[48] !== height || $[49] !== t14 || $[50] !== t15 || $[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t20 || $[55] !== togglePlayPause || $[56] !== updateProgress || $[57] !== width) {
    t21 = <video ref={videoRef} width={width} height={height} preload="none" poster={t14} title={alt} aria-label={alt} controls={false} className={t15} autoPlay={autoplay} muted={t16} data-testid="video-player" onClick={togglePlayPause} onMouseOver={handleFocus} onFocus={handleFocus} onBlur={handleFocusOut} onMouseLeave={handleFocusOut} onTimeUpdate={updateProgress} onLoadedMetadata={t17}>{t18}{t20}</video>;
    $[44] = alt;
    $[45] = autoplay;
    $[46] = handleFocus;
    $[47] = handleFocusOut;
    $[48] = height;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t20;
    $[55] = togglePlayPause;
    $[56] = updateProgress;
    $[57] = width;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== currentTime || $[60] !== duration || $[61] !== formatMessage || $[62] !== handleFocus || $[63] !== handleFocusOut || $[64] !== handleProgressKeyDown || $[65] !== handleProgressMouseDown || $[66] !== handleProgressMouseUp || $[67] !== handleVolumeChange || $[68] !== isInFocus || $[69] !== isLoaded || $[70] !== isMuted || $[71] !== isPlaying || $[72] !== progress || $[73] !== toggleMute || $[74] !== togglePlayPause || $[75] !== volume) {
    t22 = isLoaded && <fieldset className={classNames(styles["video-controls"], isInFocus && styles.active)} onMouseOver={handleFocus} onFocus={handleFocus} onBlur={handleFocusOut} onMouseLeave={handleFocusOut} data-testid="video-controls"><div className={styles["progress-bar-padding"]}><input type="range" min="0" max="1" step="0.005" className={styles["progress-bar"]} ref={progressBarRef} value={progress} alt={formatMessage("slider")} onChange={handleProgressChange} onKeyDown={handleProgressKeyDown} onMouseDown={handleProgressMouseDown} onMouseUp={handleProgressMouseUp} /></div><div className={styles["video-buttons"]}><ButtonComponent buttonType={ButtonType.AS_TEXT} iconLeft={{
          src: isPlaying ? "/assets/icons/video-pause.svg" : "/assets/icons/video-play.svg",
          alt: formatMessage(isPlaying ? "pause" : "play"),
          width: 24,
          height: 24,
          unoptimized: true
        }} iconSize={IconSize.SMALL} onClick={togglePlayPause} aria-label={formatMessage(isPlaying ? "pause" : "play")} /><div className={styles["volume-widget"]}><ButtonComponent buttonType={ButtonType.AS_TEXT} iconLeft={{
            src: isMuted ? "/assets/icons/video-mute.svg" : "/assets/icons/video-volume.svg",
            alt: formatMessage(isMuted ? "unmute" : "mute"),
            width: 24,
            height: 24,
            unoptimized: true
          }} iconSize={IconSize.SMALL} onClick={toggleMute} aria-label={formatMessage(isMuted ? "unmute" : "mute")} /><div className={styles["volume-slider"]}><input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} style={{
              "--background-size": `${volume * 100}%`
            } as React.CSSProperties} onChange={handleVolumeChange} alt={formatMessage("volume")} /></div></div><div className={styles["progress-timer"]}><span>{formatTime(currentTime)}</span><span> / </span><span>{formatTime(duration)}</span></div></div></fieldset>;
    $[59] = currentTime;
    $[60] = duration;
    $[61] = formatMessage;
    $[62] = handleFocus;
    $[63] = handleFocusOut;
    $[64] = handleProgressKeyDown;
    $[65] = handleProgressMouseDown;
    $[66] = handleProgressMouseUp;
    $[67] = handleVolumeChange;
    $[68] = isInFocus;
    $[69] = isLoaded;
    $[70] = isMuted;
    $[71] = isPlaying;
    $[72] = progress;
    $[73] = toggleMute;
    $[74] = togglePlayPause;
    $[75] = volume;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== containerStyles || $[78] !== t11 || $[79] !== t12 || $[80] !== t13 || $[81] !== t21 || $[82] !== t22) {
    t23 = <div className={styles["video-player"]} style={containerStyles}>{t11}{t12}{t13}{t21}{t22}</div>;
    $[77] = containerStyles;
    $[78] = t11;
    $[79] = t12;
    $[80] = t13;
    $[81] = t21;
    $[82] = t22;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  return t23;
}
function _temp3(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
}
function _temp2(prev) {
  return {
    progress: prev.progress - 0.05
  };
}
function _temp(prev_0) {
  return {
    progress: prev_0.progress + 0.05
  };
}